import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Inbox, Plus, UploadCloud } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { CONTRACT_STATUS, fmtDate, RISK } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const TYPES = [
  "Advisory Services Agreement", "Project Management Agreement", "Development Management Agreement",
  "Asset Management Agreement", "Construction Contract", "Architecture Agreement", "Engineering Agreement",
  "Supplier Agreement", "Professional Services Agreement", "Property Management Agreement",
  "Facility Management Agreement", "Financing Agreement", "Insurance Agreement", "Data Processing Agreement",
  "Confidentiality Agreement", "Letter of Intent", "Amendment", "Other",
];

const SOURCES = [
  { id: "manual", label: "Subida manual" },
  { id: "email", label: "Email recibido" },
  { id: "sharepoint_demo", label: "SharePoint (demo)" },
  { id: "docusign_demo", label: "DocuSign (demo)" },
];

function IntakeDialog({ open, onClose, onDone }) {
  const [form, setForm] = useState({ source: "manual", title: "", counterparty: "", contract_type: "", value: "" });
  const [dup, setDup] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setDup(null);
  }, [open]);

  const prefillFromEmail = () => {
    setForm({ source: "email", title: "Professional Services Agreement — Granvia Legal", counterparty: "Granvia Legal", contract_type: "Professional Services Agreement", value: "74000" });
  };

  const submit = async (force = false) => {
    setLoading(true);
    try {
      const payload = { ...form, value: form.value ? Number(form.value) : undefined, force, communication_id: form.source === "email" ? "com_legal_in1" : undefined };
      const r = await api.post("/legal/intake", payload);
      if (r.data.status === "duplicate_review") {
        setDup(r.data.intake);
      } else {
        toast.success(`Contrato ${r.data.contract.contract_number} recibido y expediente creado`);
        onDone(r.data.contract);
      }
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const resolve = async (decision) => {
    setLoading(true);
    try {
      const r = await api.post(`/legal/intake/${dup.id}/resolve`, { decision });
      toast.success("Decisión registrada");
      onDone(r.data.contract || null);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="intake-dialog">
        <DialogHeader><DialogTitle>{dup ? "Posible duplicado detectado" : "Recibir contrato"}</DialogTitle></DialogHeader>
        {!dup ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Canal de recepción</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger data-testid="intake-source"><SelectValue /></SelectTrigger>
                  <SelectContent>{SOURCES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo de contrato</Label>
                <Select value={form.contract_type} onValueChange={(v) => setForm({ ...form, contract_type: v })}>
                  <SelectTrigger data-testid="intake-type"><SelectValue placeholder="Clasificar…" /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Título *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="intake-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contraparte</Label><Input value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} data-testid="intake-counterparty" /></div>
              <div><Label>Valor estimado (€)</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="intake-value" /></div>
            </div>
            <button type="button" onClick={prefillFromEmail} className="text-xs font-medium text-blue-700 hover:underline" data-testid="intake-demo-email">
              Usar el email recibido de Granvia Legal (escenario 1)
            </button>
            <Button onClick={() => submit(false)} disabled={loading || !form.title.trim()} className="w-full" data-testid="intake-submit">
              <UploadCloud className="mr-1 h-4 w-4" /> {loading ? "Procesando…" : "Procesar recepción (valida duplicados)"}
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="duplicate-review">
            <p className="text-sm text-slate-600">No se ha creado ningún contrato nuevo. Se han encontrado documentos potencialmente relacionados:</p>
            {dup.candidates.map((c) => (
              <div key={c.id} className="rounded-sm border border-amber-200 bg-amber-50 p-3" data-testid={`dup-candidate-${c.id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-800">{c.contract_number} — {c.title}</p>
                  <span className="ts-chip ts-chip-warning">similitud {c.score}%</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">Estado: {CONTRACT_STATUS[c.status]?.label} · Propietario: <UserNameInline id={c.owner_id} /> · Vencimiento: {fmtDate(c.current_expiration_date)}</p>
              </div>
            ))}
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => resolve("nueva_version")} data-testid="dup-new-version">Es una nueva versión</Button>
              <Button variant="outline" onClick={() => resolve("adenda")} data-testid="dup-amendment">Es una adenda</Button>
              <Button variant="outline" onClick={() => resolve("contrato_diferente")} data-testid="dup-different">Es un contrato diferente</Button>
              <Button variant="ghost" onClick={() => resolve("cancelar")} data-testid="dup-cancel">Cancelar carga</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UserNameInline({ id }) {
  const { map } = useUsers();
  return <strong>{userName(map, id)}</strong>;
}

export default function Contratos() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { map } = useUsers();
  const [intakeOpen, setIntakeOpen] = useState(params.get("intake") === "1");
  const filters = {
    estado: params.get("estado") || "", tipo: params.get("tipo") || "", riesgo: params.get("riesgo") || "",
    q: params.get("q") || "", vencimiento: params.get("vencimiento") || "", preaviso: params.get("preaviso") || "",
  };
  const setFilter = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  const { data: items, isLoading, refetch } = useQuery({
    queryKey: ["contracts", filters],
    queryFn: async () => (await api.get("/legal/contracts", {
      params: {
        status: filters.estado || undefined, type: filters.tipo || undefined, risk: filters.riesgo || undefined,
        q: filters.q || undefined, vencimiento: filters.vencimiento || undefined, preaviso: filters.preaviso || undefined,
      },
    })).data,
  });

  const subtitle = filters.vencimiento
    ? `Contratos activos con vencimiento en los próximos ${filters.vencimiento} días`
    : filters.preaviso
      ? `Contratos con preaviso en los próximos ${filters.preaviso} días`
      : "Cartera contractual completa. Los estados solo cambian mediante acciones controladas.";

  return (
    <div data-testid="contratos-page">
      <PageHeader
        title="Contratos"
        subtitle={subtitle}
        actions={
          <>
            <Button variant="outline" onClick={() => { setIntakeOpen(true); }} data-testid="intake-button"><Inbox className="mr-1 h-4 w-4" /> Recibir contrato</Button>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <Input placeholder="Buscar por número, título, contraparte…" value={filters.q} onChange={(e) => setFilter("q", e.target.value)} className="h-8 w-72" data-testid="contract-search" />
        <Select value={filters.estado} onValueChange={(v) => setFilter("estado", v === "todos" ? "" : v)}>
          <SelectTrigger className="h-8 w-56" data-testid="contract-status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent><SelectItem value="todos">Todos los estados</SelectItem>{Object.entries(CONTRACT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.tipo} onValueChange={(v) => setFilter("tipo", v === "todos" ? "" : v)}>
          <SelectTrigger className="h-8 w-64" data-testid="contract-type-filter"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent><SelectItem value="todos">Todos los tipos</SelectItem>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.riesgo} onValueChange={(v) => setFilter("riesgo", v === "todos" ? "" : v)}>
          <SelectTrigger className="h-8 w-36" data-testid="contract-risk-filter"><SelectValue placeholder="Riesgo" /></SelectTrigger>
          <SelectContent><SelectItem value="todos">Todos</SelectItem>{Object.entries(RISK).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-500">Cargando cartera…</p>
      ) : !items?.length ? (
        <EmptyState title="Sin contratos" description="No hay contratos con estos filtros. Recibe uno nuevo con el botón superior." testid="contracts-empty" />
      ) : (
        <div className="ts-card overflow-hidden">
          <Table data-testid="contracts-table">
            <TableHeader>
              <TableRow>
                <TableHead>Contrato</TableHead>
                <TableHead className="hidden lg:table-cell">Contraparte</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="hidden md:table-cell">Riesgo</TableHead>
                <TableHead className="hidden md:table-cell">Propietario</TableHead>
                <TableHead className="hidden sm:table-cell">Vencimiento</TableHead>
                <TableHead className="hidden xl:table-cell">Valor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} className="ts-table-row" onClick={() => navigate(`/legal/contratos/${c.id}`, { state: { crumb: c.contract_number } })} data-testid={`contract-row-${c.id}`}>
                  <TableCell>
                    <p className="font-medium text-slate-900">{c.title}</p>
                    <p className="font-mono text-xs text-slate-400">{c.contract_number} · v{c.current_version} · {c.contract_type}</p>
                  </TableCell>
                  <TableCell className="hidden text-sm text-slate-600 lg:table-cell">{(c.counterparties || [])[0]?.name || "—"}</TableCell>
                  <TableCell><Chip map={CONTRACT_STATUS} value={c.status} testid={`contract-status-${c.id}`} /></TableCell>
                  <TableCell className="hidden md:table-cell"><Chip map={RISK} value={c.overall_risk} /></TableCell>
                  <TableCell className="hidden text-sm text-slate-600 md:table-cell">{userName(map, c.contract_owner)}</TableCell>
                  <TableCell className="hidden text-sm text-slate-600 sm:table-cell">{fmtDate(c.current_expiration_date)}</TableCell>
                  <TableCell className="hidden text-sm text-slate-600 xl:table-cell">{c.total_value ? `${(c.total_value / 1000).toFixed(0)} k€` : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <IntakeDialog open={intakeOpen} onClose={() => { setIntakeOpen(false); params.delete("intake"); setParams(params); }} onDone={(c) => { refetch(); setIntakeOpen(false); if (c?.id) navigate(`/legal/contratos/${c.id}`); }} />
    </div>
  );
}
